import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, Button, Dialog, Field, RelationPicker, useAction, useInvalidateResource, type FieldMeta, type ScreenContext } from '../../engine';

// "Merge into…" for a contact: pick the surviving contact (same client) with the engine's
// relation picker, then POST /v1/contacts/:id/merge { into_id }. The merged contact is deleted
// by the API, so on success we navigate to the survivor.

export function MergeContactAction({ id, row }: ScreenContext & { selected?: string[] }) {
  const [open, setOpen] = useState(false);
  const [intoId, setIntoId] = useState<string | null>(null);
  const [intoLabel, setIntoLabel] = useState<string | null>(null);
  const navigate = useNavigate();
  const invalidate = useInvalidateResource();
  const merge = useAction<{ contact: { id: string } }>('contacts', 'merge', {
    toast: { success: (r) => `Merged into ${intoLabel ?? (r as { contact: { name?: string } }).contact.name ?? 'contact'}` },
    onSuccess: (r) => {
      setOpen(false);
      invalidate('contacts');
      navigate(`/contacts/${r.contact.id}`);
    },
  });
  if (!id) return null;
  const field: FieldMeta = {
    name: 'into_id', label: 'Merge into', type: 'relation', required: true, readOnly: false, nullable: false, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: false, inList: false, inDetail: false,
    relation: { resource: 'contacts', displayField: 'name', filter: row?.client_id ? { client_id: row.client_id } : undefined },
  };
  return (
    <>
      <Button variant="danger" icon="git-branch" onClick={() => setOpen(true)}>
        Merge into…
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={`Merge ${String(row?.name ?? 'contact')} into…`} description="Tickets, activities, opportunities, site roles, tags, notes and files move to the surviving contact; blank fields are filled from this one; then this contact is deleted." locked={merge.isPending}>
        <div className="hf-stack">
          <Field label="Surviving contact" help="Only contacts of the same client can be merged." required>
            <RelationPicker field={field} value={intoId} onChange={(v, label) => { setIntoId(v); setIntoLabel(label ?? null); }} autoFocus aria-label="Surviving contact" />
          </Field>
          {intoId === id && <Banner tone="error">A contact cannot be merged into itself.</Banner>}
          <div className="hf-form-actions">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={merge.isPending}>
              Cancel
            </Button>
            <Button variant="danger" solid busy={merge.isPending} disabled={!intoId || intoId === id} onClick={() => intoId && merge.mutate({ id, body: { into_id: intoId } })}>
              Merge and delete this contact
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
